// src/pages/affiliate/DashboardPage.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { useUI } from '../../context/UIContext';
import { useAffiliate } from '../../context/AffiliateContext';
import { getBaseUrl } from '../../config/api';
import '../../styles/AffiliateDashboard.css';
import { formatCommissionPercentForDisplay } from '../../utils/affiliateFormat';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface DashboardStats {
  totalClicks: number;
  totalConversions: number;
  conversionRate: number;
  totalEarnings: number;
  pendingEarnings: number;
  availableBalance: number;
  thisMonthEarnings: number;
  lastMonthEarnings: number;
  averageCommissionRate: number;
}

interface ChartData {
  clicksData: number[];
  conversionsData: number[];
  earningsData: number[];
  labels: string[];
}

interface RecentCommission {
  _id: string;
  bookingId: {
    _id: string;
    service: string;
  };
  commissionAmount: number;
  commissionRate: number;
  status: string;
  createdAt: string;
}

const AffiliateDashboardPage: React.FC = () => {
  const { showSnackbar } = useUI();
  const { setAffiliateStatus } = useAffiliate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [recentCommissions, setRecentCommissions] = useState<RecentCommission[]>([]);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [timeRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);

      const token = localStorage.getItem('affiliateToken') || sessionStorage.getItem('affiliateToken');
      console.log('Dashboard - Token check:', { 
        hasToken: !!token, 
        tokenLength: token?.length,
        tokenPreview: token ? `${token.substring(0, 20)}...` : 'none',
        storage: localStorage.getItem('affiliateToken') ? 'localStorage' : sessionStorage.getItem('affiliateToken') ? 'sessionStorage' : 'none'
      });
      
      if (!token) {
        console.error('No affiliate token found in storage');
        showSnackbar('Please log in to view dashboard', 'error');
        return;
      }

      const apiUrl = `${getBaseUrl()}/v1/affiliate/dashboard/stats?period=${timeRange}`;
      console.log('Fetching dashboard stats from:', apiUrl);

      // Fetch stats
      const statsResponse = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Dashboard stats response:', { 
        status: statsResponse.status, 
        statusText: statsResponse.statusText,
        ok: statsResponse.ok 
      });

      const statsData = await statsResponse.json();

      if (statsResponse.ok && statsData.success) {
        const data = statsData.data;
        setStats(data);
        setChartData(data.chartData);
        if (data.affiliateStatus) {
          setAffiliateStatus(data.affiliateStatus as 'pending' | 'active' | 'suspended' | 'rejected');
        }
      } else {
        console.error('Failed to load stats:', { status: statsResponse.status, data: statsData });
        if (statsResponse.status === 401) {
          localStorage.removeItem('affiliateToken');
          sessionStorage.removeItem('affiliateToken');
          setAffiliateStatus(null);
          showSnackbar('Session expired. Please log in again.', 'error');
          window.location.href = '/affiliate/login';
          return;
        }
        showSnackbar(statsData.message || 'Failed to load stats', 'error');
      }

      // Fetch recent commissions
      const commissionsResponse = await fetch(
        `${getBaseUrl()}/v1/affiliate/commissions?limit=5&sort=-createdAt`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const commissionsData = await commissionsResponse.json();

      if (commissionsResponse.ok && commissionsData.success) {
        setRecentCommissions(commissionsData.data.commissions || []);
      } else {
        console.error('Failed to load recent commissions:', commissionsData);
      }

    } catch (error) {
      console.error('Dashboard error:', error);
      showSnackbar('An error occurred loading dashboard', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const getLineChartData = () => {
    if (!chartData || !chartData.labels || chartData.labels.length === 0) {
      // Return empty chart data structure
      return {
        labels: [],
        datasets: [
          {
            label: 'Earnings ($)',
            data: [],
            borderColor: 'rgb(102, 126, 234)',
            backgroundColor: 'rgba(102, 126, 234, 0.1)',
            fill: true,
            tension: 0.4
          },
          {
            label: 'Conversions',
            data: [],
            borderColor: 'rgb(72, 187, 120)',
            backgroundColor: 'rgba(72, 187, 120, 0.1)',
            fill: true,
            tension: 0.4,
            yAxisID: 'y1'
          }
        ]
      };
    }

    return {
      labels: chartData.labels || [],
      datasets: [
        {
          label: 'Earnings ($)',
          data: chartData.earningsData || [],
          borderColor: 'rgb(102, 126, 234)',
          backgroundColor: 'rgba(102, 126, 234, 0.1)',
          fill: true,
          tension: 0.4
        },
        {
          label: 'Conversions',
          data: chartData.conversionsData || [],
          borderColor: 'rgb(72, 187, 120)',
          backgroundColor: 'rgba(72, 187, 120, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y1'
        }
      ]
    };
  };

  const getPieChartData = () => {
    if (!stats) return null;

    return {
      labels: ['Available Balance', 'Pending Earnings', 'Paid Out'],
      datasets: [
        {
          data: [
            stats.availableBalance || 0,
            Math.max(0, (stats.pendingEarnings || 0) - (stats.availableBalance || 0)),
            Math.max(0, (stats.totalEarnings || 0) - (stats.pendingEarnings || 0))
          ],
          backgroundColor: [
            'rgba(72, 187, 120, 0.8)',
            'rgba(237, 137, 54, 0.8)',
            'rgba(102, 126, 234, 0.8)'
          ],
          borderColor: [
            'rgb(72, 187, 120)',
            'rgb(237, 137, 54)',
            'rgb(102, 126, 234)'
          ],
          borderWidth: 2
        }
      ]
    };
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Performance Overview'
      }
    },
    scales: {
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: 'Earnings ($)'
        }
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        title: {
          display: true,
          text: 'Conversions'
        },
        grid: {
          drawOnChartArea: false,
        }
      }
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      title: {
        display: true,
        text: 'Earnings Breakdown'
      }
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'approved':
        return 'badge-success';
      case 'pending':
        return 'badge-warning';
      case 'paid':
        return 'badge-info';
      case 'cancelled':
        return 'badge-error';
      default:
        return 'badge-default';
    }
  };

  if (isLoading) {
    return (
      <div className="affiliate-dashboard__loading">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="affiliate-dashboard__error">
        <p>Failed to load dashboard data</p>
        <button onClick={fetchDashboardData} className="btn-primary">Retry</button>
      </div>
    );
  }

  return (
    <div className="affiliate-dashboard">
      <div className="affiliate-dashboard__header">
        <div>
          <h1>Referral Dashboard</h1>
          <p>Track your performance and earnings</p>
        </div>
        <div className="affiliate-dashboard__actions">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as '7d' | '30d' | '90d')}
            className="affiliate-dashboard__time-select"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
          <Link to="/affiliate/marketing-materials" className="btn-primary">
            Get Marketing Materials
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="affiliate-dashboard__stats-grid">
        <div className="affiliate-dashboard__stat-card">
          <div className="stat-card__icon stat-card__icon--blue">
            <i className="fas fa-mouse-pointer"></i>
          </div>
          <div className="stat-card__content">
            <h3>Total Clicks</h3>
            <p className="stat-card__value">{(stats.totalClicks || 0).toLocaleString()}</p>
          </div>
        </div>

        <div className="affiliate-dashboard__stat-card">
          <div className="stat-card__icon stat-card__icon--green">
            <i className="fas fa-check-circle"></i>
          </div>
          <div className="stat-card__content">
            <h3>Conversions</h3>
            <p className="stat-card__value">{(stats.totalConversions || 0).toLocaleString()}</p>
            <small className="stat-card__subtitle">{((stats.conversionRate || 0).toFixed(2))}% conversion rate</small>
          </div>
        </div>

        <div className="affiliate-dashboard__stat-card">
          <div className="stat-card__icon stat-card__icon--purple">
            <i className="fas fa-dollar-sign"></i>
          </div>
          <div className="stat-card__content">
            <h3>Total Earnings</h3>
            <p className="stat-card__value">{formatCurrency(stats.totalEarnings || 0)}</p>
            <small className="stat-card__subtitle">
              {stats.lastMonthEarnings > 0 ? (
                stats.thisMonthEarnings >= stats.lastMonthEarnings ? (
                  <span className="stat-trend stat-trend--up">
                    <i className="fas fa-arrow-up"></i>
                    {((stats.thisMonthEarnings - stats.lastMonthEarnings) / stats.lastMonthEarnings * 100).toFixed(1)}%
                  </span>
                ) : (
                  <span className="stat-trend stat-trend--down">
                    <i className="fas fa-arrow-down"></i>
                    {((stats.lastMonthEarnings - stats.thisMonthEarnings) / stats.lastMonthEarnings * 100).toFixed(1)}%
                  </span>
                )
              ) : (
                <span className="stat-trend">vs last month</span>
              )}
            </small>
          </div>
        </div>

        <div className="affiliate-dashboard__stat-card stat-card--highlight">
          <div className="stat-card__icon stat-card__icon--orange">
            <i className="fas fa-wallet"></i>
          </div>
          <div className="stat-card__content">
            <h3>Available Balance</h3>
            <p className="stat-card__value">{formatCurrency(stats.availableBalance || 0)}</p>
            <Link to="/affiliate/payouts" className="stat-card__action">Request Payout →</Link>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="affiliate-dashboard__charts">
        <div className="affiliate-dashboard__chart-container">
          <div className="chart-wrapper">
            {chartData && chartData.labels && chartData.labels.length > 0 ? (
              <Line data={getLineChartData()!} options={lineChartOptions} />
            ) : (
              <div className="chart-empty-state">
                <p>No data available for the selected period</p>
                <small>Start sharing your referral link to see performance data</small>
              </div>
            )}
          </div>
        </div>

        <div className="affiliate-dashboard__chart-container affiliate-dashboard__chart-container--small">
          <div className="chart-wrapper">
            {stats && <Pie data={getPieChartData()!} options={pieChartOptions} />}
          </div>
        </div>
      </div>

      {/* Recent Commissions */}
      <div className="affiliate-dashboard__recent">
        <div className="affiliate-dashboard__section-header">
          <h2>Recent Commissions</h2>
          <Link to="/affiliate/commissions" className="btn-link">View All →</Link>
        </div>

        {recentCommissions.length === 0 ? (
          <div className="affiliate-dashboard__empty">
            <i className="fas fa-inbox"></i>
            <p>No commissions yet</p>
            <small>Start promoting to earn your first commission!</small>
          </div>
        ) : (
          <div className="affiliate-dashboard__table-container">
            <table className="affiliate-dashboard__table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Service</th>
                  <th>Rate</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentCommissions.map((commission) => (
                  <tr key={commission._id}>
                    <td>{formatDate(commission.createdAt)}</td>
                    <td>{commission.bookingId.service || 'N/A'}</td>
                    <td>{formatCommissionPercentForDisplay(commission.commissionRate)}</td>
                    <td className="amount">{formatCurrency(commission.commissionAmount)}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(commission.status)}`}>
                        {commission.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick Links */}
      <div className="affiliate-dashboard__quick-links">
        <Link to="/affiliate/profile" className="quick-link-card">
          <i className="fas fa-user"></i>
          <span>Edit Profile</span>
        </Link>
        <Link to="/affiliate/analytics" className="quick-link-card">
          <i className="fas fa-chart-line"></i>
          <span>Detailed Analytics</span>
        </Link>
        <Link to="/affiliate/marketing-materials" className="quick-link-card">
          <i className="fas fa-images"></i>
          <span>Marketing Materials</span>
        </Link>
        <Link to="/affiliate/help" className="quick-link-card">
          <i className="fas fa-question-circle"></i>
          <span>Help & Support</span>
        </Link>
      </div>
    </div>
  );
};

export default AffiliateDashboardPage;

